/**
 * Admin API Service
 * スーパー管理者向けAPIとの通信
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://struct-code-production.up.railway.app';

class AdminService {
  constructor() {
    this.client = axios.create({
      baseURL: `${API_BASE_URL}/api/admin`,
      headers: { 'Content-Type': 'application/json' },
      timeout: 30000,
    });
  }

  _getAuthHeaders() {
    const token = localStorage.getItem('struct-code-token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  _handleError(error) {
    if (error.response) {
      return { message: error.response.data.detail || 'Server error', status: error.response.status };
    } else if (error.request) {
      return { message: 'Cannot connect to server', status: 0 };
    }
    return { message: error.message || 'Unexpected error', status: -1 };
  }

  async getStats() {
    try {
      const res = await this.client.get('/stats', { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getCompanies() {
    try {
      const res = await this.client.get('/companies', { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getCompanyDetail(companyId) {
    try {
      const res = await this.client.get(`/companies/${companyId}`, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getUsage() {
    try {
      const res = await this.client.get('/usage', { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getConsumerStats() {
    try {
      const res = await this.client.get('/consumer-stats', { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getAllConversations(limit = 100) {
    try {
      const res = await this.client.get('/conversations', {
        headers: this._getAuthHeaders(),
        params: { limit },
      });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getUsers(params = {}) {
    try {
      const res = await this.client.get('/users', {
        headers: this._getAuthHeaders(),
        params,
      });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getUserDetail(userId) {
    try {
      const res = await this.client.get(`/users/${userId}`, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getUserConversations(userId) {
    try {
      const res = await this.client.get(`/users/${userId}/conversations`, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getConversationDetail(conversationId) {
    try {
      const res = await this.client.get(`/conversations/${conversationId}`, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getSubscriptions() {
    try {
      const res = await this.client.get('/billing/subscriptions', { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async grantSubscription(companyId) {
    try {
      const res = await this.client.post(`/companies/${companyId}/grant-subscription`, {}, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async revokeSubscription(companyId) {
    try {
      const res = await this.client.post(`/companies/${companyId}/revoke-subscription`, {}, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async grantUserPro(userId) {
    try {
      const res = await this.client.post(`/users/${userId}/grant-pro`, {}, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async revokeUserPro(userId) {
    try {
      const res = await this.client.post(`/users/${userId}/revoke-pro`, {}, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }
}

const adminService = new AdminService();
export default adminService;
